import React from "react";
import { useDispatch, useSelector } from "react-redux";
import Button, { ButtonSizes, ButtonThemes } from "../../../ui-components/button/button";
import Tag from "../../../ui-components/molecules/tag/tag";
import { TagColor } from "../../../app/data/common/tagColor";
import { DocumentTypes, PickupDocumentModel } from "../../../app/data/pickup/models";
import { ReactComponent as PlusIcon } from "../../../images/plus.svg";
import { ReactComponent as DownloadIcon } from "../../../images/download.svg";
import { generatePickupDocuments, pickupDetailsSelector } from "../../../slices/pickup/pickupDetailsSlice";
import Loading from "../../../ui-components/loading/loading";
import { toast } from "react-toastify";
import DocumentIcon from "../../../ui-components/atoms/document-icon/documentIcon";

import "./pickupDetails.scss";

export interface PickupDocumentsProps {
  pickupNumber: string;
  documents?: PickupDocumentModel[];
};

interface NewDocumentCardProps {
  label: string;
  onClick: () => void;
  loading: boolean;
}

const DOCUMENTS = {
  BOL: { type: "BOL", name: "Bill of Lading" },
  BOL_PHOTO: { type: "BOL", name: "Bill of Lading" },
  SHL: { type: "SHL", name: "Shipping Label" },
}

const REQUIRED_DOCUMENTS = [
  DOCUMENTS.BOL,
  DOCUMENTS.SHL,
];

const NewDocumentCard: React.FC<NewDocumentCardProps> = ({ label, onClick, loading }) => {
  return (
    <div className="xgs-pickup-details__document">
      <div className="xgs-pickup-details__document__links xgs-pickup-details__document__links--centered">
        {label}
        <Button theme={ButtonThemes.transparent} size={ButtonSizes.auto} onClick={onClick} disabled={loading}>Generate
          {loading ? <Loading isLoading={true} /> : <>&nbsp;<PlusIcon /></>}
        </Button>
      </div>
    </div>
  )
}

const PickupDetailsDocuments: React.FC<PickupDocumentsProps> = ({ documents, pickupNumber }) => {
  const dispatch = useDispatch();
  const pickupDetailsState = useSelector(pickupDetailsSelector);

  const missedDocuments = REQUIRED_DOCUMENTS.filter(type => !documents?.find(document => document.type === type.type));

  const allDocumentsMissed = missedDocuments.length === REQUIRED_DOCUMENTS.length; 

  const generateDocuments = (pickupNumber: string) => {
    if (!pickupNumber) return;
    dispatch(generatePickupDocuments(pickupNumber, () => {
      toast.info("XGS shipping labels and Bill of Lading (BoL) have been generated and sent to the shipper's email address. The reserved Probill has been assigned to the pickup.");
    }));
  }

  const bolImages = documents?.filter(document => document.type === DocumentTypes.BOL_PHOTO);

  return (
    <div className="xgs-pickup-details__section__content xgs-pickup-details__documents">
      {(documents && documents.length > 0) && (
        <>
          {documents.map((item) => {
            return (
              <a href={item.storageUrl} target="_blank" rel="noreferrer">
                <div className="xgs-pickup-details__document" key={item.id}>
                  <DocumentIcon type={item.fileType}/>
                  <div className="xgs-pickup-details__document__links">
                    <div className="xgs-pickup-details__document__header-content">
                    <div className="xgs-pickup-details__document__header">
                      <div className="xgs-pickup-details__document__name">{DOCUMENTS[item.type]?.name || item.type}</div>
                      {item.type === DocumentTypes.BOL_PHOTO && bolImages && bolImages?.length > 1 && (
                        <Tag mods={{ color: TagColor.BLUE, uppercase: true }}>
                          {bolImages.findIndex(bol => bol.id === item.id) + 1}
                        </Tag>
                      )}
                    </div>
                    {item.type === DocumentTypes.BOL_PHOTO && (
                      <div className="text-light">Uploaded by driver</div>
                    )}
                    </div>

                    <div className="xgs-pickup-details__document__pseudolink">
                      <DownloadIcon/>&nbsp;Download
                    </div>
                  </div>
                </div>
              </a>
            )
          })}
        </>
      )}

      {missedDocuments && (
        allDocumentsMissed
          ? <NewDocumentCard
            label="No documents exist"
            onClick={() => generateDocuments(pickupNumber)}
            loading={pickupDetailsState.requestStarted && pickupDetailsState.requestCreator === "GENERATE_PICKUP_DOCUMENTS"}
          />
          : <>{missedDocuments.map((document: { name: string, type: string }) => (
            <NewDocumentCard
              key={document.type}
              label={`No ${document.name} exists`} onClick={() => generateDocuments(pickupNumber)}
              loading={pickupDetailsState.requestStarted && pickupDetailsState.requestCreator === "GENERATE_PICKUP_DOCUMENTS"}
            />
          ))}</>
      )}
    </div>
  );
};

export default PickupDetailsDocuments;
